/** An app's workspace — `/apps/:id/:tab` (S3).
 *
 *  The header (icon, name, developer, storefront, category, rating, bundle id)
 *  and five tabs that are addresses: keywords, sources, competitors, score,
 *  usage. */

import { Link, Navigate, useParams } from 'react-router-dom'
import { useApp } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { AppIcon, Code, CopyButton, CountryTag, ErrorState, PageHeader, Skeleton, StatusPill } from '@/ui'
import { CompetitorsTab } from './CompetitorsTab'
import { KeywordsTab } from './KeywordsTab'
import { ScoreTab } from './ScoreTab'
import { SourcesTab } from './SourcesTab'
import { UsageTab } from './UsageTab'

export const APP_TABS = [
  { key: 'keywords', label: '키워드' },
  { key: 'sources', label: '유입' },
  { key: 'competitors', label: '경쟁사' },
  { key: 'score', label: '점수' },
  { key: 'usage', label: '사용량' },
] as const

export type AppTab = (typeof APP_TABS)[number]['key']

export function isAppTab(v: string | undefined): v is AppTab {
  return APP_TABS.some((t) => t.key === v)
}

/** The route element: reads `:tab` and sends an unknown one to the keywords. */
export function AppWorkspaceRoute() {
  const { id, tab } = useParams()
  if (!isAppTab(tab)) return <Navigate replace to={`/apps/${encodeURIComponent(id ?? '')}/keywords`} />
  return <AppWorkspace tab={tab} />
}

export function AppWorkspace({ tab }: { tab: AppTab }) {
  const { id: idParam } = useParams()
  const id = Number(idParam)
  const app = useApp(Number.isInteger(id) ? id : 0)
  const a = app.data

  const title = a ? (
    <span className="flex min-w-0 items-center gap-3">
      <AppIcon src={a.icon_url} name={a.name} size={56} />
      <span className="min-w-0">
        <span className="block truncate">{a.name}</span>
        {a.developer && <span className="block truncate text-body font-normal text-muted-foreground">{a.developer}</span>}
      </span>
    </span>
  ) : (
    '앱'
  )

  return (
    <>
      <PageHeader title={title} docTitle={a?.name ?? '앱'} parent={{ to: '/apps', label: '앱' }} crumb={a?.name}>
        {a && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-meta text-muted-foreground">
            <CountryTag code={a.country} />
            <StatusPill domain="app" status={a.is_mine ? 'mine' : 'competitor'} />
            {a.category && <span>{a.category}</span>}
            {a.rating > 0 && (
              <span className="tabular-nums">
                ★ {a.rating.toFixed(2)} · 리뷰 {a.rating_count.toLocaleString('ko-KR')}
              </span>
            )}
            {a.bundle_id && (
              <span className="inline-flex items-center gap-1">
                <Code>{a.bundle_id}</Code>
                <CopyButton text={a.bundle_id} what="번들 ID" />
              </span>
            )}
          </div>
        )}
        <nav aria-label="앱 화면" className="-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4 md:mx-0 md:px-0">
          {APP_TABS.map((t) => {
            const on = t.key === tab
            return (
              <Link
                key={t.key}
                to={`/apps/${idParam}/${t.key}`}
                aria-current={on ? 'page' : undefined}
                className={cn(
                  '-mb-px inline-flex h-10 shrink-0 items-center border-b-2 px-3 text-body font-medium transition-colors',
                  on ? 'border-accent text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {t.label}
              </Link>
            )
          })}
        </nav>
      </PageHeader>

      {app.error ? (
        <ErrorState title="앱을 불러오지 못했다" error={app.error} onRetry={() => app.refetch()} />
      ) : !a ? (
        <Skeleton className="h-40 w-full" />
      ) : tab === 'keywords' ? (
        <KeywordsTab app={a} />
      ) : tab === 'sources' ? (
        <SourcesTab app={a} />
      ) : tab === 'competitors' ? (
        <CompetitorsTab appId={a.id} />
      ) : tab === 'score' ? (
        <ScoreTab appId={a.id} />
      ) : (
        <UsageTab app={a} />
      )}
    </>
  )
}
